import { useMemo, useState } from 'react';

import { groupApi } from '@/api/services/groupApi';
import { Button, Chip, EmptyState } from '@/components/ui';
import type { ChinaAppSysGroup } from '@/types/entities';

import { AdminGroupForm } from './forms/AdminGroupForm';
import { useAdminList } from './lists/adminList';
import { MasterDetail, type MasterItem } from './MasterDetail';

/** Port von `IwmAdminGruppe.razor` in der Gestalt aus REQ-25 §4.3 (REQ-28 §3). */
const load = () => groupApi.get();

function toItem(group: ChinaAppSysGroup): MasterItem {
  return {
    id: group.Id,
    label: group.Bezeichnung ?? '',
    meta: group.IstAdministrativ ? <Chip text="Administrativ" cssClass="e-info" /> : undefined,
  };
}

export function AdminGroups() {
  const { rows, loading, error, reload } = useAdminList(load);
  // `null` heißt „nichts gewählt", `0` steht für eine neue Gruppe.
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const items = useMemo(() => rows.map(toItem), [rows]);

  const saved = async (id: number) => {
    setSelectedId(id > 0 ? id : null);
    await reload();
  };

  return (
    <MasterDetail
      listLabel="Gruppen"
      items={items}
      selectedId={selectedId}
      onSelect={setSelectedId}
      loading={loading}
      error={error}
      onRetry={() => {
        void reload();
      }}
      searchPlaceholder="Name"
      emptyText="Keine Gruppen"
      wide
      toolbar={
        <Button
          content="New"
          iconClass="fa-solid fa-plus"
          onClick={() => {
            setSelectedId(0);
          }}
        />
      }
    >
      {selectedId === null ? (
        <EmptyState
          iconClass="fa-solid fa-shield-halved"
          title="Keine Gruppe gewählt"
          description="Wählen Sie links eine Gruppe aus oder legen Sie eine neue an."
        />
      ) : (
        <AdminGroupForm id={selectedId} onSave={saved} />
      )}
    </MasterDetail>
  );
}

export default AdminGroups;
